/**
 * Client-side profile alias / avatar checks.
 *
 * Alias (incl. profanity / decamelize): keep in sync with server util/profileValidation.js.
 * Avatar allowlist: pass URLs from GET avatar-options `avatars` (active only); do not hardcode.
 */
import { profanity } from "@2toad/profanity";

export const MIN_ALIAS_LENGTH = 3;
export const MAX_ALIAS_LENGTH = 12;

const ALIAS_LETTERS_AND_SPACES = /^[A-Za-z]+( [A-Za-z]+)*$/;

/**
 * DecamelCase for profanity scans (keep in sync with server util/profileValidation.js).
 * @param {string} segment
 * @returns {string}
 */
function decamelizeSegment(segment) {
  return segment
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/**
 * @param {string} alias non-empty normalized alias
 * @returns {boolean}
 */
export function aliasContainsProfaneLanguage(alias) {
  const trimmed = alias.trim();
  if (!trimmed) return false;

  const variants = new Set();
  const add = (s) => {
    if (typeof s === "string" && s.length > 0) {
      variants.add(s);
      variants.add(s.toLowerCase());
    }
  };

  add(trimmed);

  const decamelFull = decamelizeSegment(trimmed);
  add(decamelFull);
  for (const w of decamelFull.split(/\s+/)) {
    add(w);
  }

  for (const segment of trimmed.split(/\s+/)) {
    add(segment);
    const d = decamelizeSegment(segment);
    add(d);
    for (const w of d.split(/\s+/)) {
      add(w);
    }
  }

  for (const v of variants) {
    if (profanity.exists(v)) return true;
  }
  return false;
}

/** @param {unknown} value @returns {string} */
export function normalizeAlias(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .normalize("NFKC")
    // Strip ASCII controls (mirror server normalizeAlias).
    // eslint-disable-next-line no-control-regex -- intentional removal of C0 controls + DEL
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {string} raw
 * @returns {{ ok: true, normalized: string } | { ok: false, message: string }}
 */
export function validateAlias(raw) {
  const alias = normalizeAlias(raw);

  if (alias === "") {
    return { ok: true, normalized: "" };
  }

  if (!ALIAS_LETTERS_AND_SPACES.test(alias)) {
    return {
      ok: false,
      message:
        "Alias may only contain letters and single spaces between words.",
    };
  }

  if (alias.length < MIN_ALIAS_LENGTH) {
    return {
      ok: false,
      message: `Alias must be at least ${MIN_ALIAS_LENGTH} characters.`,
    };
  }

  if (alias.length > MAX_ALIAS_LENGTH) {
    return {
      ok: false,
      message: `Alias must be ${MAX_ALIAS_LENGTH} characters or less.`,
    };
  }

  if (aliasContainsProfaneLanguage(alias)) {
    return {
      ok: false,
      message: "Alias contains disallowed language.",
    };
  }

  return { ok: true, normalized: alias };
}

/**
 * @param {string} url
 * @param {Set<string>} allowedUrls
 * @returns {boolean}
 */
export function validateAvatarUrl(url, allowedUrls) {
  return typeof url === "string" && allowedUrls.has(url);
}
